import { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  Container,
  Flex,
  Heading,
  Icon,
  Spinner,
  Stack,
  Tabs,
  Text,
} from '@chakra-ui/react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  ShareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';
import { Breadcrumbs } from '@/components/ui/breadcrumb';
import { toaster } from '@/components/ui/toaster';
import useCoursesStore from '@/utils/use-courses';
import { Course, CourseItem } from '@/lib/types';
import { slotFor } from '@/lib/slot-color';
import api from '@/utils/api';
import ContentDrawer from '@/pages/contents/content-drawer';
import ShareCourseDialog from './share-course-dialog';
import AssignCoursePanel from './assign-course-panel';
import TrackingTab from './tracking-tab';
import RemindersTab from './reminders-tab';
import VersionsTab from './versions-tab';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';

type DetailTab = 'overview' | 'tracking' | 'reminders' | 'versions';

// Course detail. Slice 1 shows the course's own fields with edit/delete for
// authors; the module overview, tracking, sharing and metrics panels land with
// their slices.
export default function CourseView() {
  const { workspaceId = '', courseId } = useParams<{ workspaceId: string; courseId: string }>();
  const navigate = useNavigate();
  const isAdmin = useIsInAdminGroup();
  const { fetchCourse, deleteCourse } = useCoursesStore();

  const [course, setCourse] = useState<Course | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [detailTab, setDetailTab] = useState<DetailTab>('overview');
  // Modules start collapsed, matching the prototype; only ids in this list are open.
  const [expandedModuleIds, setExpandedModuleIds] = useState<number[]>([]);
  const [openingItemId, setOpeningItemId] = useState<number | null>(null);
  // Content payload shown in the in-page drawer — a preview, so no completion footer.
  const [viewingContent, setViewingContent] = useState<any>(null);

  useEffect(() => {
    if (!courseId) return;
    fetchCourse(Number(courseId))
      .then(setCourse)
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const toggleModule = (moduleId: number) =>
    setExpandedModuleIds((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId],
    );

  const openItem = async (item: CourseItem) => {
    if (!item.content_reference_id) return;
    setOpeningItemId(item.id);
    try {
      const response = await api.get(`public/content/${item.content_reference_id}`);
      const content = response.data.data;
      if (content) setViewingContent(content);
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to open content',
        type: 'error',
      });
    } finally {
      setOpeningItemId(null);
    }
  };

  const handleDelete = async () => {
    if (!course) return;
    if (!window.confirm(`Delete "${course.name}"? This removes all completion records too.`)) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteCourse(course.id);
      toaster.create({ title: 'Course deleted', type: 'success' });
      navigate(`/${workspaceId}/courses`);
    } catch {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Center minH='50vh'>
        <Spinner size='lg' />
      </Center>
    );
  }

  if (!course) {
    return (
      <Center minH='50vh'>
        <Text fontSize='sm' color='fg.muted'>
          This course doesn&apos;t exist or you don&apos;t have access to it.
        </Text>
      </Center>
    );
  }

  const isPublished = course.status === 'published';

  return (
    <Box minH='100vh' mx='auto' width={{ md: '90%' }} maxW='6xl' px={{ base: 4, md: 0 }} py={6}>
      <Container>
        <Breadcrumbs
          items={[{ label: 'Courses', href: `/${workspaceId}/courses` }, { label: course.name }]}
        />

        <Flex align='flex-end' justify='space-between' gap={4} mt={2} mb={5}>
          <Box>
            <Flex align='center' gap={2.5} mb={1.5}>
              <Badge
                bg={isPublished ? 'success.soft' : 'bg.subtle'}
                color={isPublished ? 'success' : 'fg.muted'}
                textTransform='uppercase'
                fontSize='2xs'
              >
                {course.status}
              </Badge>
              <Text fontSize='xs' color='fg.muted'>
                {course.version > 0 && `Version ${course.version} · `}
                {course.module_count} modules · {course.item_count} items
              </Text>
            </Flex>
            <Heading as='h1' size='3xl'>
              {course.name}
            </Heading>
          </Box>
          {course.can_edit && (
            <Flex gap={2.5} flexShrink={0}>
              <Button
                variant='ghost'
                size='sm'
                onClick={() => navigate(`/${workspaceId}/courses/${course.id}/learn`)}
              >
                Preview as learner
              </Button>
              {isPublished && (
                <Button variant='outline' size='sm' onClick={() => setShareOpen(true)}>
                  <Icon as={ShareIcon} w={4} h={4} />
                  Share
                </Button>
              )}
              <Button variant='outline' size='sm' onClick={handleDelete} loading={isDeleting}>
                <Icon as={TrashIcon} w={4} h={4} />
                Delete
              </Button>
              <Button
                variant='primary'
                size='sm'
                onClick={() => navigate(`/${workspaceId}/courses/${course.id}/build`)}
              >
                <Icon as={PencilSquareIcon} w={4} h={4} />
                Edit course
              </Button>
            </Flex>
          )}
        </Flex>

        {course.description && (
          <Text fontSize='sm' color='fg.muted' maxW='2xl' mb={6}>
            {course.description}
          </Text>
        )}

        <ShareCourseDialog
          course={course}
          isOpen={shareOpen}
          onClose={() => setShareOpen(false)}
          onShared={(updated) => setCourse((prev) => (prev ? { ...prev, ...updated } : updated))}
        />

        {isAdmin && isPublished && (
          <Tabs.Root
            value={detailTab}
            onValueChange={(e) => setDetailTab(e.value as DetailTab)}
            variant='line'
            size='md'
            mb={5}
          >
            <Tabs.List>
              {(['overview', 'tracking', 'reminders', 'versions'] as const).map((tab) => (
                <Tabs.Trigger
                  key={tab}
                  value={tab}
                  _selected={{ color: 'brand.fg', fontWeight: '700' }}
                  textTransform='capitalize'
                >
                  {tab}
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Tabs.Root>
        )}

        {detailTab === 'tracking' && isAdmin ? (
          <TrackingTab course={course} />
        ) : detailTab === 'reminders' && isAdmin ? (
          <RemindersTab course={course} />
        ) : detailTab === 'versions' && isAdmin ? (
          <VersionsTab
            course={course}
            onPublished={(updated) => setCourse((prev) => (prev ? { ...prev, ...updated } : updated))}
          />
        ) : (
        <>
        <Box
          bg='bg.panel'
          borderWidth='1px'
          borderColor='border'
          borderRadius='lg'
          shadow='sm'
          p={6}
        >
          <Text
            fontFamily='heading'
            fontSize='2xs'
            fontWeight='700'
            textTransform='uppercase'
            letterSpacing='0.16em'
            color='fg.muted'
            mb={4}
          >
            Modules
          </Text>

          {(course.modules ?? []).length === 0 && (
            <Text fontSize='sm' color='fg.muted' textAlign='center' py={6}>
              No modules yet — open the builder to add the first one.
            </Text>
          )}

          <Stack gap={2}>
            {(course.modules ?? []).map((module, index) => {
              const isOpen = expandedModuleIds.includes(module.id);
              return (
                <Box key={module.id} borderWidth='1px' borderColor='border' borderRadius='md'>
                  <Flex
                    align='center'
                    gap={3}
                    p={3}
                    cursor='pointer'
                    onClick={() => toggleModule(module.id)}
                  >
                    <Center
                      w='26px'
                      h='26px'
                      borderRadius='full'
                      bg='bg.subtle'
                      fontFamily='heading'
                      fontSize='xs'
                      fontWeight='700'
                      color='fg'
                      flexShrink={0}
                    >
                      {index + 1}
                    </Center>
                    <Box minW={0} flex='1'>
                      <Text fontSize='sm' fontWeight='600' color='fg.strong' truncate>
                        {module.name || 'Untitled module'}
                      </Text>
                      <Text fontSize='xs' color='fg.muted'>
                        {(module.items ?? []).length}{' '}
                        {(module.items ?? []).length === 1 ? 'item' : 'items'}
                        {module.assignment_text && ' · assignment'}
                      </Text>
                    </Box>
                    <Icon
                      as={ChevronDownIcon}
                      w={4}
                      h={4}
                      color='fg.subtle'
                      transform={isOpen ? 'rotate(180deg)' : undefined}
                      transition='transform 0.2s'
                    />
                  </Flex>

                  {isOpen && (
                    <Box px={3} pb={3}>
                      {module.instructions && (
                        <Box bg='bg.subtle' borderRadius='md' px={3.5} py={3} mb={2}>
                          <Text
                            fontFamily='heading'
                            fontSize='2xs'
                            fontWeight='700'
                            textTransform='uppercase'
                            letterSpacing='0.16em'
                            color='fg.muted'
                            mb={1}
                          >
                            How to complete this module
                          </Text>
                          <Text fontSize='sm' color='fg'>
                            {module.instructions}
                          </Text>
                        </Box>
                      )}

                      <Stack gap={1.5}>
                        {(module.items ?? []).map((item) => {
                          const slot = slotFor(item.content_type_name);
                          // content_id goes null when the library item is deleted (the reference
                          // id is a snapshot), so it's the real availability signal.
                          const openable = !!item.content_id && !!item.content_reference_id;
                          return (
                            <Flex
                              key={item.id}
                              as={openable ? 'button' : 'div'}
                              onClick={openable ? () => openItem(item) : undefined}
                              aria-busy={openingItemId === item.id}
                              w='full'
                              textAlign='left'
                              align='center'
                              gap={3}
                              px={3}
                              py={2.5}
                              borderWidth='1px'
                              borderColor='border'
                              borderRadius='md'
                              cursor={openable ? 'pointer' : undefined}
                              transition='border-color 0.15s, background 0.15s'
                              _hover={openable ? { borderColor: 'border.emphasized', bg: 'bg.subtle' } : undefined}
                            >
                              <Box minW={0} flex='1'>
                                <Text fontSize='sm' fontWeight='600' color='fg.strong' truncate>
                                  {item.content_name}
                                </Text>
                                <Flex align='center' gap={2} mt={0.5}>
                                  {item.content_type_name && (
                                    <Badge
                                      bg={`slot${slot}.bg`}
                                      color={`slot${slot}.fg`}
                                      fontSize='2xs'
                                      textTransform='uppercase'
                                    >
                                      {item.content_type_name}
                                    </Badge>
                                  )}
                                  {item.source_label && item.source_label !== 'My workspace' && (
                                    <Text fontSize='xs' color='fg.muted'>
                                      {item.source_label}
                                    </Text>
                                  )}
                                  {!item.content_id && (
                                    <Text fontSize='xs' color='fg.subtle'>
                                      This item is no longer available in the library
                                    </Text>
                                  )}
                                </Flex>
                              </Box>
                              {openable &&
                                (openingItemId === item.id ? (
                                  <Spinner size='xs' color='fg.subtle' />
                                ) : (
                                  <Icon as={ChevronRightIcon} w={4} h={4} color='fg.subtle' />
                                ))}
                            </Flex>
                          );
                        })}
                        {(module.items ?? []).length === 0 && (
                          <Text fontSize='xs' color='fg.muted' px={1} py={1}>
                            No items in this module yet.
                          </Text>
                        )}
                      </Stack>

                      {module.assignment_text && (
                        <Box
                          bg='bg.subtle'
                          borderRadius='md'
                          px={3.5}
                          py={3}
                          mt={2}
                          borderWidth='1px'
                          borderColor='border'
                          borderStyle='dashed'
                        >
                          <Text
                            fontFamily='heading'
                            fontSize='2xs'
                            fontWeight='700'
                            textTransform='uppercase'
                            letterSpacing='0.16em'
                            color='fg.muted'
                            mb={1}
                          >
                            Assignment
                          </Text>
                          <Text fontSize='sm' color='fg'>
                            {module.assignment_text}
                          </Text>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Box>

        {isAdmin && isPublished && (
          <Box mt={5}>
            <AssignCoursePanel
              course={course}
              workspaceId={workspaceId}
              onAssigned={() =>
                setCourse((prev) =>
                  prev ? { ...prev, tracking_locked: true, shared_at: prev.shared_at ?? new Date().toISOString() } : prev,
                )
              }
            />
          </Box>
        )}
        </>
        )}
      </Container>

      <ContentDrawer
        isOpen={!!viewingContent}
        onClose={() => setViewingContent(null)}
        content={viewingContent}
        drawerId='course-view'
      />
    </Box>
  );
}
